"use client";

import { useEffect } from "react";

/**
 * Journey script is initialized for a document, not via our legacy GAM/Nitro
 * route-refresh code. Until a supported Journey SPA route API is confirmed,
 * navigate to/from base detail pages with a real document load so Journey can
 * initialize once against the correct DOM and URL.
 *
 * This is a deliberate full-document navigation fallback, NOT an attempt to
 * invoke an undocumented Journey refresh API or inject its script twice.
 */
export default function JourneyBaseNavigation() {
  useEffect(() => {
    const onClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.hasAttribute("download") ||
          (anchor.target && anchor.target !== "_self") ||
          anchor.dataset.journeySpa === "true") return;

      let next: URL;
      try { next = new URL(anchor.href, window.location.href); }
      catch { return; }

      const current = new URL(window.location.href);
      if (next.origin !== current.origin || !/^https?:$/.test(next.protocol)) return;
      if (next.pathname === current.pathname && next.search === current.search) return;

      // Restrict the fallback to navigations involving monetized base pages.
      // Do not force full reloads for the rest of the application.
      const isBase = (path: string) => path === "/bases" || path.startsWith("/bases/");
      if (!isBase(current.pathname) && !isBase(next.pathname)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(next.href);
    };

    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, []);

  return null;
}
